import { message } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomSwitch, CustomTypography } from "sipli-fleet-core/components";
import {
  useMutationSaveTachographSetting,
  useQueryGetTachographSetting,
} from "src/queries/tachographSettings.queries";
import TachographActivationModal from "../TachographActivationModal";

interface TachographSettingsProps {
  vehicleId?: string;
}

const TachographSettings = ({ vehicleId }: TachographSettingsProps) => {
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [draftEnabled, setDraftEnabled] = useState<boolean | null>(null);
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);
  const [modalMode, setModalMode] = useState<"activated" | "deactivate" | null>(null);
  const currentVehicleId = useRef(vehicleId);
  currentVehicleId.current = vehicleId;
  const settingQuery = useQueryGetTachographSetting(vehicleId, {
    refetchInterval: pendingEnabled !== null ? 2000 : false,
  });
  const saveMutation = useMutationSaveTachographSetting();
  const settingData = settingQuery.data?.data;
  const setting = settingData && "response" in settingData ? settingData.response : settingData;
  const serviceEnabled = setting?.enabled === true;
  const formEnabled = draftEnabled ?? serviceEnabled;
  const busy = saveMutation.isPending || pendingEnabled !== null;
  const unavailable = !vehicleId || settingQuery.isLoading || settingQuery.isError;

  useEffect(() => {
    setDraftEnabled(null);
    setPendingEnabled(null);
    setModalMode(null);
  }, [vehicleId]);

  useEffect(() => setDraftEnabled(null), [setting?.enabled]);

  useEffect(() => {
    if (pendingEnabled === null || !setting || settingQuery.isError) return;
    if (setting.enabled !== pendingEnabled) return;
    setPendingEnabled(null);
    if (!pendingEnabled) messageApi.success(t("tachograph_vehicle_disabled"));
  }, [pendingEnabled, setting, settingQuery.isError, messageApi, t]);

  const save = async (enabled = formEnabled) => {
    if (!vehicleId || unavailable || busy) return;
    const nextEnabled = enabled;
    try {
      await saveMutation.mutateAsync({ vehicleId, payload: { enabled: nextEnabled } });
      if (currentVehicleId.current !== vehicleId) return;
      setDraftEnabled(nextEnabled);
      setPendingEnabled(nextEnabled);
      setModalMode(nextEnabled ? "activated" : null);
    } catch {
      if (currentVehicleId.current !== vehicleId) return;
      setDraftEnabled(null);
      setModalMode(null);
      messageApi.error(t("failed_to_update_tachograph_setting"));
    }
  };

  const handleSwitchChange = (enabled: boolean) => {
    if (enabled) {
      setDraftEnabled(true);
      void save(true);
    } else {
      setModalMode("deactivate");
    }
  };

  return (
    <div className="rounded-[16px] bg-white p-[24px]">
      {contextHolder}
      <CustomTypography.CustomText className="!block !text-[19.93px] !leading-[28px] !font-bold !text-black">
        {t("tachograph_settings")}
      </CustomTypography.CustomText>
      <div className="mt-[24px] flex items-center justify-between gap-[24px] rounded-[8px] bg-gray1 p-[16px]">
        <div className="flex min-w-0 flex-col gap-[12px]">
          <CustomTypography.CustomText id="vehicle-tachograph-activation" className="!text-[17.72px] !leading-[24px] !font-semibold !text-black">
            {t("tachograph_activation")}
          </CustomTypography.CustomText>
          <CustomTypography.CustomText className="!text-[14px] !leading-[18px] !text-gray8">
            {t("tachograph_vehicle_activation_description")}
          </CustomTypography.CustomText>
        </div>
        <CustomSwitch aria-labelledby="vehicle-tachograph-activation" checked={formEnabled}
          loading={busy} disabled={unavailable || busy} onChange={handleSwitchChange} />
      </div>
      {pendingEnabled !== null && <p role="status" className="mt-[16px] text-gray8">{t("tachograph_processing")}</p>}
      <TachographActivationModal scope="vehicle" mode={modalMode}
        loading={saveMutation.isPending} onConfirm={() => void save(false)}
        onClose={() => {
          if (saveMutation.isPending) return;
          if (modalMode === "deactivate") setDraftEnabled(null);
          setModalMode(null);
        }} />
    </div>
  );
};

export default TachographSettings;
